import type { IconAsset, IconSourceData, Resolution, Slide } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type { AssetContainerModel } from 'js/src/client';
import { AssetContainerTypes, SourceEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    getSceneColorShuffleData,
    getSceneColorShuffleIndex,
    isSceneColorShuffleAvailable,
} from 'js/src/data/color-shuffle';
import { getActiveSearchContextForCurrentTab, SEARCH_CONTEXT } from 'js/src/data/media';
import { getFocusedSubsceneIndex, trackChangedTextAssetSlide } from 'js/src/data/scenes';
import { getTemplateBundle, getTemplateDefaultIconSceneDesign } from 'js/src/data/templates';
import { immutableAssetContainerModelIsIcon } from 'js/src/data/uploads';
import { checkpointVideo } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import type { ImmutableMap } from 'js/src/libs/consts';
import { SIMPLE_ICON_COLOR_THRESHOLD } from 'js/src/libs/consts'; // we consider icon as simple less than this number of colors
import { focusAssetElement } from 'js/src/libs/custom-utils';
import type { StartEditingNonTextAssetPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    addStickerAssetToSceneOperation,
    getIconType,
    getNumNonOutroNonBrandSceneGroups,
    getOrCreateStickerCompatibleSceneOperation,
    getSwappableIconAssets,
    IconType,
    isSceneAnOverlay,
    isVoiceDrivenSceneGroup,
    saveRawLuminary,
    sceneContainsIcon,
} from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { AddSceneGroupInfo } from 'js/src/luminary/luminary-types';
import { getTargetIndex } from 'js/src/luminary/luminary-utils';

/**
 * Update a color property of an icon asset
 */
export const setIconColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetName: string,
        colorPropertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return;
        scene
            .getAssetsByName(assetName)
            .filter(VideoData.isIconAsset)
            .forEach((asset) => setObjectPropertyValue(asset, colorPropertyName, newColor));
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Assigns the provided iconSourceData object to the specified scene.
 * If no targetAssetId is specified we'll assign the icon to the 'default' (aka first editable) icon asset component
 * on the scene. If none exists we'll switch to a scene design that does support icons.
 */
export const setIconAssetForSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneId: string,
        iconSourceData: ImmutableMap<AssetContainerModel>,
        {
            targetAssetId = null,
            targetPosition = null,
            targetDimensions = null,
            isDrag = false,
        }: {
            targetAssetId?: string | null;
            targetPosition?: { x: number; y: number } | null;
            targetDimensions?: Resolution | null;
            isDrag?: boolean;
        } = {},
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        // validate arguments
        let scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        if (!immutableAssetContainerModelIsIcon(iconSourceData)) {
            Toast.error('Invalid icon source data', 'Error');
            return;
        }

        const rawSourceData = iconSourceData.toJS();
        const iconAssetSourceData = rawSourceData.icon;
        const searchContext = getActiveSearchContextForCurrentTab(getState());

        const tryAddNewIconAssetToScene = (
            scene: Slide,
            { createNewScene } = { createNewScene: false },
        ) => {
            let newScene = null;
            let iconAsset = null;
            if (isVoiceDrivenSceneGroup(scene.sceneGroup) && !isSceneAnOverlay(getState(), scene)) {
                if (!createNewScene) {
                    return { newScene, iconAsset };
                }
                const stickerCompatibleScene = call(
                    getOrCreateStickerCompatibleSceneOperation,
                    sceneId,
                    systemTriggered,
                    parentActionGroup,
                );
                if (!stickerCompatibleScene) {
                    return { newScene, iconAsset };
                }
                newScene = stickerCompatibleScene;
                sceneId = stickerCompatibleScene.id;
            }
            iconAsset = call(
                addStickerAssetToSceneOperation,
                sceneId,
                VideoData.ICON_ASSET_TYPE,
                targetPosition,
                targetDimensions,
            ) as IconAsset;
            return { iconAsset, newScene };
        };

        const tryGetOrAddIconAsset = (scene: Slide) => {
            let targetAsset = targetAssetId ? scene.getAssetById(targetAssetId) : undefined;
            if (targetAsset && !VideoData.isIconAsset(targetAsset)) {
                targetAsset = undefined;
            }

            const targetAssetIconType = targetAsset ? getIconType(targetAsset) : undefined;
            const sourceIconType =
                searchContext === SEARCH_CONTEXT.WAVEFORMS ? IconType.WAVEFORM : IconType.REGULAR;

            let iconAsset = null;
            let newScene = null;
            if (!!targetAsset && targetAssetIconType === sourceIconType) {
                return { iconAsset: targetAsset, newScene };
            }

            // No target asset specified
            // If targetPosition is provided, add as sticker (dropped on scene)
            if (targetPosition) {
                ({ iconAsset, newScene } = tryAddNewIconAssetToScene(scene, {
                    createNewScene: true,
                }));
            } else {
                // No target asset and no target position - try to find default icon to replace
                const allSwappableNonWaveformIconAssets = getSwappableIconAssets(
                    scene,
                    sourceIconType,
                );

                if (allSwappableNonWaveformIconAssets.length > 0) {
                    return { iconAsset: allSwappableNonWaveformIconAssets[0], newScene };
                } else {
                    ({ iconAsset, newScene } = tryAddNewIconAssetToScene(scene));
                }
            }

            if (iconAsset && sourceIconType === IconType.WAVEFORM) {
                iconAsset.playbackMode = VideoData.PLAYBACK_WAVEFORM;
                // TODO: Please remove the line below once luminary merges this ticket:
                // https://lumen5.atlassian.net/browse/LU-3877
                scene.requiresUpdate = true;
            }
            return { iconAsset, newScene };
        };

        // get the icon to update
        // if no specific icon has been specified then update first editable icon asset
        // unless we are in sticker tab, stickers always create a new asset
        const { iconAsset, newScene } = tryGetOrAddIconAsset(scene);
        if (!iconAsset) {
            return;
        }
        if (newScene) {
            scene = newScene;
            sceneId = newScene.id;
        }

        // assign sourceData
        if (
            iconAssetSourceData.num_colors === 0 &&
            iconAssetSourceData.source === SourceEnum.SOURCE_LUMEN5_ICONS_PREMIUM
        ) {
            // some of uploaded lumen5 icons white and doesn't have num_colors value in DB, so we need to replace sourceData to apply existing colorMap
            iconAsset.replace(iconAssetSourceData as IconSourceData); // will use old colorMap
        } else if (
            iconAssetSourceData.num_colors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length === iconAssetSourceData.num_colors
        ) {
            // basic case: simple icon replace with similar simple icon
            iconAsset.replace(iconAssetSourceData as IconSourceData); // if icon has less than 3 colors rewrite colors with old colorMap
        } else if (
            iconAssetSourceData.num_colors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length > SIMPLE_ICON_COLOR_THRESHOLD
        ) {
            const state = getState();
            const isColorShuffleAvaliable = isSceneColorShuffleAvailable(state, sceneId);
            // we're using a "data" Luminary instance, an instance of Luminary which we're configuring to be fast so we can use it to pull info
            // Even with these optimizations it takes 30-50ms to create so we shouldn't abuse it!
            const dataLuminary = new Luminary({
                fetchResources: false,
                disableRenderer: true,
                autoDraw: false,
                autoUpdate: false,
            });

            const templateBundle = getTemplateBundle(state, luminary.template.id).toJS();

            dataLuminary.setTemplate(templateBundle);

            const targetSceneDesignId = scene.sceneDesign;
            const dataScene = dataLuminary.addScene({
                sceneDesignId: targetSceneDesignId,
            });

            // now we can just pull data from the data scene!
            const templateIconAsset = dataScene.getAssetByName(iconAsset.name) as IconAsset;
            if (templateIconAsset) {
                let iconIndexColor = VideoData.getTemplateIndexFromAssetProperty(
                    templateIconAsset,
                    'colorMaps.0.targetColor',
                );

                if (isColorShuffleAvaliable) {
                    const colorShuffleIndex = getSceneColorShuffleIndex(state, sceneId);
                    const colorShuffleData = getSceneColorShuffleData(state, sceneId);
                    if (colorShuffleData) {
                        iconIndexColor =
                            colorShuffleData.colorShuffles[colorShuffleIndex][iconIndexColor];
                    }
                }
                setObjectPropertyValue(
                    iconAsset,
                    'colorMaps[0].targetColor',
                    VideoData.templateColorByIndex(iconIndexColor),
                ); // we modify old map to use original color from template and then replace icons.
            }

            iconAsset.replace(iconAssetSourceData as IconSourceData);

            void dataLuminary.destroy();
        } else {
            iconAsset.sourceData = iconAssetSourceData as IconSourceData; // this will create new colorMap from icon using custom colors
            iconAsset.slide.attribution = iconAssetSourceData.attribution ?? '';
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // analytics
        analytics.track('Icon Added To Scene', {
            sceneId: sceneId,
            method: isDrag ? 'drag' : 'click',
            media_source: searchContext,
            current_user: iconSourceData
                .get(AssetContainerTypes.ICON)
                .get('history_index_user_ids')
                ?.some((user_id: number) => SERVER.USER.id === user_id),
            is_waveform: searchContext === SEARCH_CONTEXT.WAVEFORMS,
        });
        if (!systemTriggered) {
            const subsceneIndex = getFocusedSubsceneIndex(getState());
            SlideEventEmitter.emit(SLIDE_EVENTS.START_EDITING_NON_TEXT_ASSET, {
                sceneGroupId: scene.sceneGroup.id,
                sceneId,
                subsceneIndex,
                assetId: iconAsset.id,
                assetType: VideoData.ICON_ASSET_TYPE,
            } as StartEditingNonTextAssetPayload);
            focusAssetElement(iconAsset.id, sceneId, subsceneIndex ?? 0);
        }
    },
);

/**
 * Fetches the specified asset from a scene and functionally "empties" its source data so
 * it doesn't display anything.
 */
export const clearIconAssetSourceDataOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        const iconAsset = scene.getAssetById(assetId) as IconAsset | undefined;

        if (!iconAsset) {
            return;
        }

        if (!VideoData.isIconAsset(iconAsset)) {
            throw new Error('Provided asset is not an icon.');
        }

        iconAsset.sourceData = VideoData.INVISIBLE_LOTTIE;

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Icon Asset cleared', { sceneId });
    },
);

/**
 * Creates a blank scene and assigns the IconAsset to the first IconAsset
 * @return {Object} sceneDetails {sceneId, sceneGroupId}
 */
export const addSceneFromIconOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        targetIndex: number,
        iconSourceData: ImmutableMap<AssetContainerModel>,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): AddSceneGroupInfo => {
        // find default icon scene design
        const templateId = luminary.template.id;
        const sceneDesignId = getTemplateDefaultIconSceneDesign(getState(), templateId);

        if (!sceneDesignId) {
            throw new Error('no default_icon_scene_design found, unable to create scene!');
        }
        if (!immutableAssetContainerModelIsIcon(iconSourceData)) {
            throw new Error('Adding scene from icon which is not an icon!');
        }
        const numNonOutroNonBrandSceneGroups = getNumNonOutroNonBrandSceneGroups(luminary);

        // create scene, apply icon, and move to desired index
        const { scene, sceneGroup } = luminary.addSceneGroup({ sceneDesignId });
        call(
            setIconAssetForSceneOperation,
            scene.id,
            iconSourceData,
            undefined,
            systemTriggered,
            parentActionGroup,
        );
        targetIndex = getTargetIndex(luminary, targetIndex);
        luminary.moveSceneGroupToIndex(sceneGroup.id, targetIndex);
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // analytics
        let mediaSource;
        switch (iconSourceData.getIn([iconSourceData.get('type'), 'source'])) {
            case SourceEnum.SOURCE_USER:
            case SourceEnum.SOURCE_USER_WATERMARK:
            case SourceEnum.SOURCE_USER_OUTRO:
                mediaSource = 'Uploads';
                break;
            case SourceEnum.SOURCE_ARTICLE:
                mediaSource = 'Article';
                break;
            default:
                mediaSource = 'Library';
                break;
        }
        analytics.track('New Scene Added', {
            method: 'Icon Drag and Drop',
            mediaSource: mediaSource,
            sceneId: scene.id,
            sceneGroupId: sceneGroup.id,
            sceneDesignId: scene.sceneDesign,
            sceneCategory: scene.sceneCategory,
            templateId: luminary.template.id,
            hasIcon: sceneContainsIcon(scene),
            numNonOutroNonBrandSceneGroups,
        });

        // track that the scene has been edited
        dispatch(trackChangedTextAssetSlide({ sceneId: scene.id }));

        return { sceneId: scene.id, sceneGroupId: sceneGroup.id };
    },
);
